<script setup lang="ts">
import { nextTick, ref } from 'vue';
import type { DeviceActionRequest } from '@atum-remote/protocol/device-actions';
import type { DirectoryDevice } from '../lib/directory.js';
import AppIcon from './AppIcon.vue';

const props = defineProps<{ busy: boolean }>();
const emit = defineEmits<{
  confirm: [device: DirectoryDevice, action: DeviceActionRequest['action']];
  close: [];
}>();
const dialog = ref<HTMLDialogElement>();
const cancelButton = ref<HTMLButtonElement>();
const target = ref<DirectoryDevice>();
const action = ref<DeviceActionRequest['action']>('lock');
const labels = { lock: 'Lock', restart: 'Restart', shutdown: 'Shut down' };
const explanation = {
  lock: 'The screen will lock. Apps and terminal sessions can keep running.',
  restart: 'Active connections will end while this device restarts. Save any open work first.',
  shutdown: 'Active connections will end. Someone may need to turn this device back on locally.',
};
async function open(device: DirectoryDevice, requested: DeviceActionRequest['action']) {
  target.value = device;
  action.value = requested;
  dialog.value?.showModal();
  await nextTick();
  cancelButton.value?.focus();
}
function close() {
  dialog.value?.close();
}
function confirm() {
  if (target.value && !props.busy) emit('confirm', target.value, action.value);
}
defineExpose({ open, close });
</script>
<template>
  <dialog
    ref="dialog"
    class="dialog"
    aria-labelledby="device-action-title"
    aria-describedby="device-action-description"
    @close="emit('close')"
    @cancel="props.busy && $event.preventDefault()"
  >
    <form v-if="target" class="dialog-body" @submit.prevent="confirm">
      <header class="dialog-head">
        <div>
          <p class="eyebrow">Device action</p>
          <h2 id="device-action-title">{{ labels[action] }} {{ target.name }}?</h2>
        </div>
        <button
          type="button"
          class="icon-button quiet"
          aria-label="Close confirmation"
          :disabled="props.busy"
          @click="close"
        >
          <AppIcon name="close" />
        </button>
      </header>
      <p class="muted">
        {{ target.hostname }} · {{ target.platform === 'macos' ? 'macOS' : 'Windows' }}
      </p>
      <p id="device-action-description">{{ explanation[action] }}</p>
      <p v-if="action !== 'lock'" class="muted">
        Open apps can ask to save work or prevent shutdown. This action does not force them to
        close.
      </p>
      <p v-if="target.platform === 'macos'" class="muted">
        macOS may require Automation permission on this device.
      </p>
      <footer class="dialog-actions">
        <button ref="cancelButton" type="button" :disabled="props.busy" @click="close">
          Cancel
        </button>
        <button
          type="submit"
          :class="action === 'lock' ? 'primary' : 'danger'"
          :disabled="props.busy"
        >
          {{ props.busy ? 'Sending request…' : labels[action] + ' ' + target.name }}
        </button>
      </footer>
    </form>
  </dialog>
</template>
